/* ==========================================================================
   Cart line purchase-option control — behaviour.

   Markup: snippets/cart-line-plan.liquid. Styles: assets/cart-line-plan.css.
   Loaded once from layout/theme.liquid.

   🔴 EVERY LISTENER IS DELEGATED FROM `document`, ON PURPOSE.
   The drawer rebuilds its item list by fetching templates/cart.ajax.liquid,
   parsing it with DOMParser and appending the parsed nodes (see
   `CartForm.prototype.cartMarkup` in assets/theme.js). Nodes that arrive that
   way are brand new, and any <script> inside them never executes. A handler
   bound to a specific trigger would therefore survive exactly until the first
   quantity change. Delegation is the only binding that keeps working.

   🔴 THE PLAN AND THE `_subscription` PROPERTY MOVE IN ONE REQUEST.
   Ticket 05 (manna-dew-app/app/utils/subscription-cart.ts and
   /api/carrier-service) decides free delivery from the `_subscription: "true"`
   line-item property ALONE — Shopify's carrier-service payload carries no
   selling-plan field. So:

     switch to one-time  ⇒ selling_plan: null  AND  drop  _subscription
     switch to a cadence ⇒ selling_plan: <id>  AND  set   _subscription: "true"

   A stale marker with no plan ships a one-time order free of charge. A plan
   with no marker charges a subscriber for delivery they were promised free.
   Both keys go in the SAME /cart/change.js body, which applies whole or not at
   all. Never split this into two calls.

   Verified against the live cart on 2026-08-24, not taken from the docs:

   - `id: "<line key>"` DOES change a selling plan. The Ajax API reference says
     selling-plan updates need `line:`; the running cart accepts the line key on
     `id:` just as happily, and targets it unambiguously even when a second line
     holds the same variant on a different plan. `id:` is used here because the
     LINE INDEX IS NOT STABLE — changing a line re-keys it and Shopify is free
     to reorder, so an index captured at render time can point at the wrong line
     by the time the customer clicks.
   - `selling_plan: null` clears the plan. So does `""`. OMITTING the key does
     NOT — it leaves the plan intact (also recorded in
     specs/2026-07-31-subscriptions/handoff.md, T1).
   - Sending `properties` REPLACES the whole bag for that line, so the current
     bag is re-read from /cart.js immediately before the write and passed back
     with only `_subscription` touched. The numbered box contents ("1", "2", …)
     and the delivery widget's `_order_delivery_*` keys must survive; they are
     what the customer built and what the app reads.
   - An EMPTY `properties` object is a no-op. `{}` does not clear a bag — it is
     ignored, and a line whose only property was `_subscription: "true"` kept the
     marker while losing its plan. See the guard in `applyPlan`.
   ========================================================================== */

(function () {
  'use strict';

  var ROOT_SEL = '[data-cart-plan]';
  var OPTION_SEL = '[data-cart-plan-option]';

  var openRoot = null;

  /* ------------------------------ small helpers ---------------------------- */

  function options(root) {
    return Array.prototype.slice.call(root.querySelectorAll(OPTION_SEL));
  }

  function listOf(root) {
    return root.querySelector('[data-cart-plan-list]');
  }

  function triggerOf(root) {
    return root.querySelector('[data-cart-plan-trigger]');
  }

  function selectedIndex(root) {
    var all = options(root);
    for (var i = 0; i < all.length; i++) {
      if (all[i].getAttribute('aria-selected') === 'true') return i;
    }
    return 0;
  }

  function showError(root, message) {
    var el = root.querySelector('[data-cart-plan-error]');
    if (!el) return;
    if (message) {
      el.textContent = message;
      el.hidden = false;
    } else {
      el.textContent = '';
      el.hidden = true;
    }
  }

  /* --------------------------------- open/close ---------------------------- */

  function open(root, focusIndex) {
    if (openRoot && openRoot !== root) close(openRoot, false);

    var list = listOf(root);
    var trigger = triggerOf(root);
    if (!list || !trigger) return;

    showError(root, '');
    list.hidden = false;
    root.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    openRoot = root;

    // The drawer's item list is the scroll container, so a panel opened on the
    // last line can land below the fold. 'nearest' nudges it into view without
    // yanking the whole drawer.
    if (list.scrollIntoView) list.scrollIntoView({ block: 'nearest' });

    if (typeof focusIndex === 'number') {
      var all = options(root);
      var target = all[focusIndex] || all[0];
      if (target) target.focus();
    }
  }

  function close(root, returnFocus) {
    if (!root) return;
    var list = listOf(root);
    var trigger = triggerOf(root);
    if (list) list.hidden = true;
    root.classList.remove('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (openRoot === root) openRoot = null;
    if (returnFocus && trigger) trigger.focus();
  }

  function moveFocus(root, from, delta) {
    var all = options(root);
    if (!all.length) return;
    var next = (from + delta + all.length) % all.length;
    all[next].focus();
  }

  /* ------------------------------- cart writes ----------------------------- */

  function planIdOf(item) {
    var alloc = item && item.selling_plan_allocation;
    return alloc && alloc.selling_plan ? String(alloc.selling_plan.id) : '';
  }

  function samePropertyBag(a, b) {
    a = a || {};
    b = b || {};
    var ka = Object.keys(a);
    var kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    for (var i = 0; i < ka.length; i++) {
      if (!(ka[i] in b)) return false;
      if (String(a[ka[i]]) !== String(b[ka[i]])) return false;
    }
    return true;
  }

  /* A plan change re-keys the line, so the key rendered into the DOM is stale the
     moment the write succeeds. Find the line again in the response: exact
     property match first, then variant + plan as a fallback (Shopify drops
     properties whose value is blank, which would defeat an exact compare). */
  function findChangedLine(cart, variantId, planId, sentProps) {
    var items = (cart && cart.items) || [];
    var i;
    for (i = 0; i < items.length; i++) {
      if (String(items[i].variant_id) !== String(variantId)) continue;
      if (planIdOf(items[i]) !== String(planId || '')) continue;
      if (samePropertyBag(items[i].properties, sentProps)) return items[i];
    }
    for (i = 0; i < items.length; i++) {
      if (String(items[i].variant_id) !== String(variantId)) continue;
      if (planIdOf(items[i]) === String(planId || '')) return items[i];
    }
    return null;
  }

  /* The invariant this whole control exists to protect. Loud in the console if
     it is ever broken, because the failure is invisible in the UI and costs the
     bakery a delivery. */
  function assertMarkerMatchesPlan(item) {
    if (!item) return;
    var hasPlan = planIdOf(item) !== '';
    var props = item.properties || {};
    var hasMarker = String(props._subscription) === 'true';
    if (hasPlan !== hasMarker) {
      console.error(
        '[cart-line-plan] selling plan and _subscription disagree on line ' +
          item.key +
          ' — plan=' +
          planIdOf(item) +
          ', _subscription=' +
          props._subscription
      );
    }
  }

  function waitForTrigger(key, timeout) {
    return new Promise(function (resolve) {
      var deadline = Date.now() + (timeout || 4000);
      var sel = '[data-key="' + key.replace(/"/g, '\\"') + '"] [data-cart-plan-trigger]';

      function attempt() {
        var el = document.querySelector(sel);
        if (el) {
          observer.disconnect();
          resolve(el);
          return true;
        }
        if (Date.now() > deadline) {
          observer.disconnect();
          resolve(null);
          return true;
        }
        return false;
      }

      var observer = new MutationObserver(attempt);
      if (attempt()) return;
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(function () {
        observer.disconnect();
        resolve(document.querySelector(sel));
      }, timeout || 4000);
    });
  }

  /* 🔴 THE RENDERED KEY GOES STALE, so it is never trusted on its own.
     Any property write re-keys a line, and the delivery widget rewrites
     `_order_delivery_*` on every line whenever the customer touches the delivery
     controls — with no guarantee the cart re-renders afterwards. Observed live
     2026-08-24: a selection silently failed because the key in the DOM no longer
     existed. The 1-based index is what the theme's own quantity stepper and
     remove button use, and a property write keeps a line's position, so it is
     the right fallback — guarded by a variant-id check so a reordered cart makes
     the control refuse rather than rewrite the wrong line. */
  function resolveLine(cart, root) {
    var key = root.getAttribute('data-line-key');
    var variantId = root.getAttribute('data-variant-id');
    var index = parseInt(root.getAttribute('data-line'), 10);
    var items = cart.items || [];
    var i;

    for (i = 0; i < items.length; i++) {
      if (items[i].key === key) return items[i];
    }

    var byIndex = items[index - 1];
    if (
      byIndex &&
      String(byIndex.variant_id) === String(variantId) &&
      planIdOf(byIndex) === (root.getAttribute('data-current-plan') || '')
    ) {
      return byIndex;
    }

    return null;
  }

  function applyPlan(root, planId, restoreFocus) {
    var variantId = root.getAttribute('data-variant-id');

    root.classList.add('is-busy');
    showError(root, '');

    var sentProps = null;

    fetch('/cart.js?t=' + Date.now(), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var line = resolveLine(cart, root);
        if (!line) throw new Error('This item is no longer in your cart.');

        // Read the CURRENT bag and hand it straight back. `properties` replaces
        // the whole bag, so anything dropped here is destroyed: the numbered box
        // contents the customer built, and the delivery widget's own keys.
        sentProps = {};
        var props = line.properties || {};
        Object.keys(props).forEach(function (k) {
          sentProps[k] = props[k];
        });

        if (planId) {
          sentProps._subscription = 'true';
        } else {
          delete sentProps._subscription;
          // 🔴 AN EMPTY `properties` OBJECT IS A NO-OP, NOT A CLEAR.
          // Proven on the live cart 2026-08-24: a line whose ONLY property was
          // `_subscription: "true"`, sent `{selling_plan: null, properties: {}}`,
          // came back with the plan gone and the marker STILL "true" — a one-time
          // line that ships free. Shopify only replaces the bag when the object
          // has at least one key. A blank value is the clear: the key survives
          // as "", and ticket 05 tests `=== "true"`, so a blank marker is
          // correctly read as "not a subscription".
          if (!Object.keys(sentProps).length) sentProps._subscription = '';
        }

        return fetch('/cart/change.js?t=' + Date.now(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            id: line.key,
            quantity: line.quantity,
            selling_plan: planId ? Number(planId) : null,
            properties: sentProps
          })
        });
      })
      .then(function (res) {
        return res.json().then(function (body) {
          if (!res.ok) {
            throw new Error(body.description || body.message || 'Could not update this item.');
          }
          return body;
        });
      })
      .then(function (cart) {
        var changed = findChangedLine(cart, variantId, planId, sentProps);
        assertMarkerMatchesPlan(changed);

        close(root, false);

        // The theme's own refresh path. `cart:build` re-fetches
        // templates/cart.ajax.liquid into every CartForm (drawer and /cart page);
        // `cart:updated` is what the delivery widget listens on. Same pair, same
        // order, as CartForm.quantityChanged in assets/theme.js.
        document.dispatchEvent(new CustomEvent('cart:build'));
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: { cart: cart, source: 'cart-line-plan' }
          })
        );

        if (!restoreFocus || !changed) return;
        waitForTrigger(changed.key).then(function (el) {
          if (el) el.focus();
        });
      })
      .catch(function (err) {
        root.classList.remove('is-busy');
        showError(root, err && err.message ? err.message : 'Could not update this item.');
      });
  }

  function select(option, viaKeyboard) {
    var root = option.closest(ROOT_SEL);
    if (!root) return;

    var planId = option.getAttribute('data-plan') || '';
    if (planId === (root.getAttribute('data-current-plan') || '')) {
      close(root, viaKeyboard);
      return;
    }
    applyPlan(root, planId, viaKeyboard);
  }

  /* -------------------------------- listeners ------------------------------ */

  /* Escape is shared. `theme.Drawers` closes the whole cart drawer on Escape, and
     a customer pressing Escape to dismiss this panel does not mean "throw my cart
     off screen" — verified 2026-08-24: without this the drawer closed and focus
     landed on <body>.

     🔴 STOPPING THE KEYDOWN IS NOT ENOUGH. The drawer listens on `keyup`
     (assets/theme.js, `Drawers.bindEvents`), which is a different event and
     arrives after this one no matter what is stopped here. The matching keyup is
     therefore swallowed too, from a CAPTURE-phase listener on window so it runs
     before the drawer's bubble-phase one. Only the keyup that follows an Escape
     this control actually handled is swallowed, so Escape still closes the
     drawer whenever no panel is open. */
  var swallowNextEscapeKeyup = false;

  function stopIfHandled(evt) {
    evt.stopPropagation();
    if (evt.stopImmediatePropagation) evt.stopImmediatePropagation();
    swallowNextEscapeKeyup = true;
  }

  window.addEventListener(
    'keyup',
    function (evt) {
      if (!swallowNextEscapeKeyup) return;
      swallowNextEscapeKeyup = false;
      if (evt.key !== 'Escape' && evt.key !== 'Esc' && evt.keyCode !== 27) return;
      evt.stopPropagation();
      if (evt.stopImmediatePropagation) evt.stopImmediatePropagation();
    },
    true
  );

  function forgetDetachedRoot() {
    if (openRoot && !document.documentElement.contains(openRoot)) openRoot = null;
  }

  document.addEventListener('click', function (evt) {
    forgetDetachedRoot();
    var option = evt.target.closest ? evt.target.closest(OPTION_SEL) : null;
    if (option) {
      evt.preventDefault();
      select(option, false);
      return;
    }

    var trigger = evt.target.closest ? evt.target.closest('[data-cart-plan-trigger]') : null;
    if (trigger) {
      evt.preventDefault();
      var root = trigger.closest(ROOT_SEL);
      if (!root) return;
      if (root.classList.contains('is-open')) {
        close(root, false);
      } else {
        open(root, null);
      }
      return;
    }

    if (openRoot && !openRoot.contains(evt.target)) close(openRoot, false);
  });

  document.addEventListener('keydown', function (evt) {
    forgetDetachedRoot();
    var target = evt.target;
    if (!target || !target.closest) return;

    var trigger = target.closest('[data-cart-plan-trigger]');
    if (trigger) {
      var root = trigger.closest(ROOT_SEL);
      if (!root) return;
      if (evt.key === 'ArrowDown' || evt.key === 'Enter' || evt.key === ' ' || evt.key === 'Spacebar') {
        evt.preventDefault();
        open(root, selectedIndex(root));
      } else if (evt.key === 'ArrowUp') {
        evt.preventDefault();
        open(root, options(root).length - 1);
      } else if (evt.key === 'Escape' && root.classList.contains('is-open')) {
        evt.preventDefault();
        stopIfHandled(evt);
        close(root, true);
      }
      return;
    }

    var option = target.closest(OPTION_SEL);
    if (!option) {
      if (evt.key === 'Escape' && openRoot) {
        stopIfHandled(evt);
        close(openRoot, true);
      }
      return;
    }

    var optRoot = option.closest(ROOT_SEL);
    if (!optRoot) return;
    var all = options(optRoot);
    var index = all.indexOf(option);

    switch (evt.key) {
      case 'ArrowDown':
        evt.preventDefault();
        moveFocus(optRoot, index, 1);
        break;
      case 'ArrowUp':
        evt.preventDefault();
        moveFocus(optRoot, index, -1);
        break;
      case 'Home':
        evt.preventDefault();
        if (all[0]) all[0].focus();
        break;
      case 'End':
        evt.preventDefault();
        if (all[all.length - 1]) all[all.length - 1].focus();
        break;
      case 'Enter':
      case ' ':
      case 'Spacebar':
        evt.preventDefault();
        select(option, true);
        break;
      case 'Escape':
        evt.preventDefault();
        stopIfHandled(evt);
        close(optRoot, true);
        break;
      case 'Tab':
        close(optRoot, false);
        break;
      default:
        break;
    }
  });

  // Any cart rebuild throws away the node `openRoot` points at. Nothing tells us
  // when that has happened — `cartMarkup` in assets/theme.js emits no event of
  // its own — so the reference is checked rather than trusted.
  document.addEventListener('cart:build', forgetDetachedRoot);
})();
